import { Color, MeshStandardMaterial, Vector4, type IUniform, type WebGLProgramParametersWithUniforms } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "../shaders/chunks.ts";
import type { CharacterSurfaceStyle } from "./config.ts";
import { injectAtChunk } from "./inject.ts";

/**
 * Material da superfície dos atores (F118, ADR 0025): um `MeshStandardMaterial` clonado e corrigido no
 * `onBeforeCompile`. O PBR do three continua inteiro (cor por vértice, rugosidade, BRDF); a correção
 * tinge a máscara na cor do acento, troca a cor pelo vidro (o herói), soma no emissivo a costura e o
 * Fresnel na cor do acento, alimenta a luz do PBR com a atmosfera (`ACTOR_LIGHT`, sem luzes do three)
 * e traz da cena o que os outros materiais já têm: a névoa da atmosfera, o rim de luz (e o frio da
 * noite nas assombrações), a máscara do contorno e o dissolve (`USE_DISSOLVE`).
 *
 * Fase 15 (pedido do dono, referência `in-game.png`): facetas limpas, sem as linhas dos triângulos nem
 * os nós dos cantos (saíram o baricêntrico, a máscara dos cantos e o trecho "mint").
 *
 * Tudo que muda em jogo é uniform: a chave do programa é a mesma para qualquer cor ou ator, e só as
 * defines do three (skinning, instâncias, cor por vértice) e o dissolve criam programas.
 */

/** Chave do programa (as defines entram na chave pelo próprio three). */
export const ENERGY_PROGRAM_KEY = "eldrun-character-surface-v3";

/** Nome dos materiais (agrupa na `drawBreakdown()` e nas sondas). */
export const ENERGY_MATERIAL_NAME = "ator";

/** Rugosidade e metal do PBR dos atores: tecido, couro e pelo foscos (a cor por vértice não tem mapa). */
export const ACTOR_PBR = { roughness: 0.85, metalness: 0 } as const;

/**
 * A luz da atmosfera no PBR (revisão da F118): sem luzes do three. As contagens de luz da cena entram
 * na chave do programa de TODO material do three (`WebGLPrograms`), então luzes só para os atores
 * mudariam o programa de toda a cena e duplicariam programas nas cenas sem elas (os ícones, as
 * vitrines). A correção alimenta as equações de luz do próprio PBR (`RE_Direct`, a `irradiance`) com os
 * uniforms da atmosfera: o sol, o preenchimento frio do lado oposto (`uFill`) e o hemisfério céu/chão.
 * O three divide a luz por π (`BRDF_Lambert`): as forças levam π de volta, com os pesos do toon (sol
 * 0,85, ambiente 0,55; o preenchimento, sem o corte da sombra do toon, a 0,5).
 */
export const ACTOR_LIGHT = { key: 0.85, fill: 0.5, ambient: 0.55 } as const;

/** Uniforms próprios de cada material da superfície (objetos dele; os da atmosfera entram por referência). */
export interface EnergyUniforms {
  /** Cor do acento (linear): costura, Fresnel, vidro e o tingimento da máscara. */
  uEnergyColor: IUniform<Color>;
  /** 1 ligado, 0 desligado (sobra o PBR com a cor original e o rim de luz). */
  uEnergyEnabled: IUniform<number>;
  uEnergySeam: IUniform<number>;
  uEnergyFresnel: IUniform<number>;
  /** Vidro (Fase 13): 0 a 1. */
  uEnergyGlass: IUniform<number>;
  /**
   * Luminância de referência do tingimento da máscara (`tintColors`, F47): a média das partes tingíveis
   * vira a cor do acento, mantendo o claro e o escuro do atlas. 0 = sem tingimento.
   */
  uEnergyTintRef: IUniform<number>;
  /** Rim de luz da atmosfera (o de sempre; 3× nas reforçadas) e o frio da noite (assombrações). */
  uRim: IUniform<number>;
  uRimNightOn: IUniform<number>;
  /** Máscara do contorno (personagem = 1). */
  uOutline: IUniform<number>;
  /** Dissolve (0 inteiro, 1 sumido; negativo sem a borda) e a cor da borda (rgb). */
  uDissolve: IUniform<number>;
  uDissolveEdge: IUniform<Vector4>;
}

export interface EnergyLook {
  /** Rim de luz (padrão 0,8, o dos avatares). */
  rim?: number;
  /** Rim frio da noite (assombrações). */
  nightRim?: boolean;
  /** Luminância de referência do tingimento da máscara (0 ou ausente: sem tingimento). */
  tintRef?: number;
}

/** Uniforms novos (objetos próprios) com o estilo e o visual pedidos. */
export function energyUniforms(style: CharacterSurfaceStyle, look: EnergyLook = {}): EnergyUniforms {
  const u: EnergyUniforms = {
    uEnergyColor: { value: new Color() },
    uEnergyEnabled: { value: 0 },
    uEnergySeam: { value: 0 },
    uEnergyFresnel: { value: 0 },
    uEnergyGlass: { value: 0 },
    uEnergyTintRef: { value: look.tintRef ?? 0 },
    uRim: { value: look.rim ?? 0.8 },
    uRimNightOn: { value: look.nightRim ? 1 : 0 },
    uOutline: { value: 1 },
    uDissolve: { value: 0 },
    uDissolveEdge: { value: new Vector4() },
  };
  writeEnergyStyle(u, style, style.accentColor);
  return u;
}

/** Escreve o estilo nos uniforms (sem recompilar nada); `colorHex` é a cor já no modo de jogo em uso. */
export function writeEnergyStyle(u: EnergyUniforms, style: CharacterSurfaceStyle, colorHex: string): void {
  u.uEnergyColor.value.set(colorHex);
  const c = u.uEnergyColor.value;
  u.uDissolveEdge.value.set(c.r, c.g, c.b, 1);
  u.uEnergyEnabled.value = style.enabled ? 1 : 0;
  u.uEnergySeam.value = style.seamIntensity;
  u.uEnergyFresnel.value = style.fresnel;
  u.uEnergyGlass.value = style.glass;
}

const VERTEX_DECLARATIONS = /* glsl */ `
attribute float tint;
varying float vEnergyTint;
varying vec3 vEnergyWorldPos;
`;

const VERTEX_ATTRIBUTES = /* glsl */ `
vEnergyTint = tint;
`;

// Posição no mundo do vértice deformado (skin) com a instância do lote: a névoa e o dissolve. O
// `worldpos_vertex` do three só existe com mapa de ambiente, sombra ou transmissão.
const VERTEX_WORLD = /* glsl */ `
vec4 energyWorld = vec4(transformed, 1.0);
#ifdef USE_BATCHING
  energyWorld = batchingMatrix * energyWorld;
#endif
#ifdef USE_INSTANCING
  energyWorld = instanceMatrix * energyWorld;
#endif
vEnergyWorldPos = (modelMatrix * energyWorld).xyz;
`;

/** Trechos da cena que o fragmento da superfície reaproveita (névoa, rim, contorno, dissolve). */
export const ENERGY_FRAGMENT_CHUNKS = ["atmo", "rimNight", "fogGlow", "outline", "dissolve", "sunShadow"] as const;

const FRAGMENT_DECLARATIONS = /* glsl */ `
${assemble("fragment", ENERGY_FRAGMENT_CHUNKS)}
varying float vEnergyTint;
varying vec3 vEnergyWorldPos;
uniform vec3 uEnergyColor;
uniform float uEnergyEnabled;
uniform float uEnergySeam;
uniform float uEnergyFresnel;
uniform float uEnergyGlass;
uniform float uEnergyTintRef;
uniform vec4 uFill;
const vec3 ENERGY_LUMA = vec3(0.2126, 0.7152, 0.0722);
// A máscara na cor do acento mantendo o claro e o escuro do atlas (a conta de \`tintColors\`, F47): a
// luminância média das partes tingíveis (uEnergyTintRef) vira a cor pedida; acima de 1 num canal, a
// cor inteira é limitada pelo maior canal (o brilho satura sem mudar o matiz).
vec3 energyTint(vec3 color, float mask) {
  if (mask <= 0.0 || uEnergyTintRef <= 0.0) return color;
  float shade = dot(color, ENERGY_LUMA) / uEnergyTintRef;
  float peak = max(max(uEnergyColor.r, uEnergyColor.g), uEnergyColor.b);
  float k = min(shade, peak > 0.0 ? 1.0 / peak : shade);
  return mix(color, uEnergyColor * k, mask);
}
`;

const FRAGMENT_TINT = /* glsl */ `
diffuseColor.rgb = energyTint(diffuseColor.rgb, vEnergyTint);
// Vidro (Fase 13): fosco pela luminância do modelo (o claro e o escuro do atlas continuam legíveis); a
// cor original some. Fase 15: sem as linhas, a cor da classe vem do próprio vidro (o herói de vidro
// verde da referência): metade na cor do acento no corpo, quase toda na máscara da roupa.
float glassLuma = sqrt(clamp(dot(diffuseColor.rgb, ENERGY_LUMA) * 1.8, 0.0, 1.0));
vec3 glassTone = uEnergyColor / max(max(uEnergyColor.r, uEnergyColor.g), max(uEnergyColor.b, 1e-3));
vec3 glassBase = mix(vec3(0.09), vec3(0.34), glassLuma) * mix(vec3(1.0), glassTone, mix(0.5, 0.9, vEnergyTint));
diffuseColor.rgb = mix(diffuseColor.rgb, glassBase, uEnergyGlass);
`;

// O vidro reflete mais o sol (menos rugoso, um pouco de metal).
const FRAGMENT_GLASS_PBR = /* glsl */ `
roughnessFactor = mix(roughnessFactor, 0.32, uEnergyGlass);
metalnessFactor = mix(metalnessFactor, 0.12, uEnergyGlass);
`;

// Depois do emissivo do three (e da normal): o rim de luz, a costura, o Fresnel e a luz do vidro.
const FRAGMENT_ENERGY = /* glsl */ `
vec3 energyView = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);
// Rim de luz proporcional ao albedo (normalizado pela luminância): o matiz do material, nunca o inverso.
totalEmissiveRadiance += rimLight(normal, energyView) * diffuseColor.rgb / max(dot(diffuseColor.rgb, ENERGY_LUMA), 1e-3);
float energyFresnel = pow(1.0 - clamp(dot(normal, energyView), 0.0, 1.0), 3.5);
// A máscara acende por igual na face (Fase 15: sem as linhas, nada de quina mais acesa).
float energySeam = vEnergyTint * 0.35 * uEnergySeam;
// Vidro: o Fresnel forte no acento (borda clara puxando ao branco) e um brilho interno (a luz que
// atravessa o corpo translúcido da referência; Fase 15: 0,02 → 0,08, a leitura que as linhas davam).
vec3 energyGlassLight = uEnergyGlass * (uEnergyColor * (energyFresnel * 0.7 + 0.08) + vec3(energyFresnel * 0.3));
totalEmissiveRadiance += uEnergyEnabled * (uEnergyColor * (energySeam + energyFresnel * uEnergyFresnel) + energyGlassLight);
`;

// A luz da atmosfera nas equações do PBR (depois da preparação da geometria da luz, antes do fim que
// aplica a irradiância): sol e preenchimento como luzes diretas, o hemisfério na irradiância.
const FRAGMENT_LIGHTS = /* glsl */ `
IncidentLight energyLight;
energyLight.visible = true;
energyLight.direction = normalize((viewMatrix * vec4(uLightDir, 0.0)).xyz);
// Sombra do sol (G3): a normal de volta ao mundo (a transposta da rotação da vista).
energyLight.color = uLightColor * (${ACTOR_LIGHT.key.toFixed(2)} * PI) * sunShadow(vEnergyWorldPos, normalize((vec4(geometryNormal, 0.0) * viewMatrix).xyz));
RE_Direct(energyLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight);
energyLight.direction = normalize((viewMatrix * vec4(-uLightDir.x, max(uFill.y, 0.05), -uLightDir.z, 0.0)).xyz);
energyLight.color = uAmbientSky * (uFill.x * ${ACTOR_LIGHT.fill.toFixed(2)} * PI);
RE_Direct(energyLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight);
vec3 energyUp = normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz);
irradiance += mix(uAmbientGround, uAmbientSky, 0.5 * dot(geometryNormal, energyUp) + 0.5) * (${ACTOR_LIGHT.ambient.toFixed(2)} * PI);
`;

// Antes do tom e do espaço de cor (a ordem da superfície): a borda do dissolve e a névoa da atmosfera.
const FRAGMENT_FOG = /* glsl */ `
#ifdef USE_DISSOLVE
  float energyGrain = dissolveGrain(vEnergyWorldPos);
  gl_FragColor.rgb = dissolveEdge(gl_FragColor.rgb, energyGrain);
#endif
gl_FragColor.rgb = applyFog(gl_FragColor.rgb, vEnergyWorldPos, cameraPosition);
`;

// No fim (depois de todas as derivadas): a máscara do contorno no alfa e o descarte do dissolve.
const FRAGMENT_END = /* glsl */ `
gl_FragColor.a = outlineAlpha();
#ifdef USE_DISSOLVE
  if (energyGrain < abs(uDissolve)) discard;
#endif
`;

/** As fontes do `MeshStandardMaterial` com a superfície (lança erro se faltar algum chunk do three). */
export function patchEnergyShaders(shader: { vertexShader: string; fragmentShader: string }): { vertexShader: string; fragmentShader: string } {
  let vertex = injectAtChunk(shader.vertexShader, "common", VERTEX_DECLARATIONS, "after");
  vertex = injectAtChunk(vertex, "begin_vertex", VERTEX_ATTRIBUTES, "after");
  vertex = injectAtChunk(vertex, "project_vertex", VERTEX_WORLD, "after");
  let fragment = injectAtChunk(shader.fragmentShader, "common", FRAGMENT_DECLARATIONS, "after");
  fragment = injectAtChunk(fragment, "color_fragment", FRAGMENT_TINT, "after");
  fragment = injectAtChunk(fragment, "metalnessmap_fragment", FRAGMENT_GLASS_PBR, "after");
  fragment = injectAtChunk(fragment, "emissivemap_fragment", FRAGMENT_ENERGY, "after");
  fragment = injectAtChunk(fragment, "lights_fragment_begin", FRAGMENT_LIGHTS, "after");
  fragment = injectAtChunk(fragment, "tonemapping_fragment", FRAGMENT_FOG, "before");
  fragment = injectAtChunk(fragment, "dithering_fragment", FRAGMENT_END, "after");
  return { vertexShader: vertex, fragmentShader: fragment };
}

/** Material PBR de partida dos atores: cor por vértice (modelos) ou sólida (bonecos), facetado, sem a névoa do three. */
export function characterBaseMaterial(o: { vertexColors?: boolean; color?: string } = {}): MeshStandardMaterial {
  return new MeshStandardMaterial({
    name: ENERGY_MATERIAL_NAME,
    vertexColors: o.vertexColors === true,
    ...(o.color !== undefined && { color: o.color }),
    roughness: ACTOR_PBR.roughness,
    metalness: ACTOR_PBR.metalness,
    // Normal por face pela derivada (os modelos não têm normal): o low-poly facetado de sempre.
    flatShading: true,
    // A névoa é a da atmosfera (no shader); a do three ficaria dobrada se uma cena a ligasse.
    fog: false,
  });
}

/** Uniforms e atmosfera de um material da superfície (para os setters e os testes). */
export interface EnergyMaterialData {
  energy: EnergyUniforms;
  atmosphere: AtmosphereUniforms;
}

/**
 * Fora do `userData`: o `clone` do three copia o `userData` por JSON (os uniforms e a atmosfera
 * inteira virariam cópias mortas).
 */
const materialData = new WeakMap<object, EnergyMaterialData>();

/** Os uniforms da superfície de um material (ou `undefined` se não é da superfície). */
export function energyData(material: object): EnergyMaterialData | undefined {
  return materialData.get(material);
}

/**
 * Clone de `base` com a superfície: o gancho entra DEPOIS do clone (o three não garante copiar
 * callbacks), com os uniforms da atmosfera por referência e os `uniforms` próprios. `defines` a mais
 * (o dissolve) criam outro programa, como a variante de dissolve da superfície.
 */
export function createEnergyMaterial(base: MeshStandardMaterial, atmosphere: AtmosphereUniforms, uniforms: EnergyUniforms, defines: Record<string, string> = {}): MeshStandardMaterial {
  const material = base.clone();
  material.name = base.name || ENERGY_MATERIAL_NAME;
  material.defines = { ...base.defines, ...defines };
  materialData.set(material, { energy: uniforms, atmosphere });
  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    Object.assign(shader.uniforms, atmosphere, uniforms);
    const patched = patchEnergyShaders(shader);
    shader.vertexShader = patched.vertexShader;
    shader.fragmentShader = patched.fragmentShader;
  };
  material.customProgramCacheKey = () => ENERGY_PROGRAM_KEY;
  return material;
}

/**
 * Variante de um material da superfície: os mesmos uniforms (por referência) com `uniforms` próprios
 * por cima e `defines` a mais. O dissolve de um corpo tem o próprio limiar; o resto segue o original.
 */
export function energyVariant(material: MeshStandardMaterial, variant: { defines?: Record<string, string>; uniforms?: Partial<EnergyUniforms> }): MeshStandardMaterial {
  const data = energyData(material);
  if (!data) throw new Error("superfície dos atores: o material não é da superfície");
  return createEnergyMaterial(material, data.atmosphere, { ...data.energy, ...variant.uniforms }, { ...(material.defines as Record<string, string>), ...variant.defines });
}
